import { reactive } from 'vue';
import ReportsAPI from 'kanban/api/reports';

const createReportState = () => ({
  data: null,
  isLoading: false,
  error: false,
  // Monotonic token so a slow earlier request can't overwrite a newer one's result.
  requestToken: 0,
});

/**
 * Page-scoped fetching for the Kanban reports. Each report keeps its own
 * data/isLoading/error so one failing widget never takes down the whole tab, and
 * tabs can fetch lazily (only what the active tab needs). Reports data is not shared
 * across the app, so this lives in a composable rather than the global Vuex store.
 */
export function useKanbanReports() {
  const reports = reactive({
    summary: createReportState(),
    timeseries: createReportState(),
    funnel: createReportState(),
    cfd: createReportState(),
    aging: createReportState(),
    agentPerformance: createReportState(),
    stageDurations: createReportState(),
  });

  const run = async (key, apiCall) => {
    const report = reports[key];
    report.requestToken += 1;
    const token = report.requestToken;
    report.isLoading = true;
    report.error = false;
    try {
      const { data } = await apiCall();
      if (report.requestToken !== token) return;
      report.data = data;
    } catch {
      if (report.requestToken !== token) return;
      report.error = true;
    } finally {
      // Only the latest request clears the spinner; stale ones leave it to the newer call.
      if (report.requestToken === token) report.isLoading = false;
    }
  };

  return {
    reports,
    fetchSummary: params => run('summary', () => ReportsAPI.summary(params)),
    fetchTimeseries: params =>
      run('timeseries', () => ReportsAPI.timeseries(params)),
    fetchFunnel: params => run('funnel', () => ReportsAPI.funnel(params)),
    fetchCfd: params => run('cfd', () => ReportsAPI.cfd(params)),
    fetchAging: params => run('aging', () => ReportsAPI.aging(params)),
    fetchAgentPerformance: params =>
      run('agentPerformance', () => ReportsAPI.agentPerformance(params)),
    fetchStageDurations: params =>
      run('stageDurations', () => ReportsAPI.stageDurations(params)),
  };
}
